import {
	BusyIndicator,
	Dialog,
	FormattedText,
	Portal,
	Toolbar
} from "client/Components";
import { DetailsTileModel, getTileDetails } from "client/Api/Workspace/workspace";
import {
	AlertManager,
	useAlerts,
	useDataInputDialog
} from "client/Hooks";
import { ensureNotNil } from "client/Misc/utils";
import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { HttpRequestError } from "client/Api/httpClient";
import { TileEditMode } from "./common";
import { EditTile, EditTileHandle } from "./EditTile";
import styles from "./EditTileSlider.module.less";

export type EditTileSliderProps = {
	id: number;
	onAfterTileEdited: () => void;
	onClose: () => void;
};

export const EditTileSlider = (props: EditTileSliderProps): ReactElement => {
	const {
		id,
		onAfterTileEdited,
		onClose
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(true);
	const [model, setModel] = useState<DetailsTileModel | null>(null);
	const editTileRef: RefObject<EditTileHandle | null> = useRef<EditTileHandle>(null);

	const alertManager: AlertManager = useAlerts();

	const tileEditMode = useMemo(
		(): TileEditMode => {
			return {
				kind: "edit",
				id
			};
		},
		[id]
	);

	useEffect(
		(): (() => void) => {
			const load = async (id: number, signal: AbortSignal): Promise<void> => {
				try {
					setIsBusy(true);
					const model: DetailsTileModel = await getTileDetails(id, signal);
					setModel(model);
					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					const messageKey: LocalizationKey = (e instanceof HttpRequestError && e.statusCode === 403)
						? "_YouDoNotHavePermissionForThat"
						: "_GeneralErrorMessage";

					alertManager.showError(localize(messageKey));
					onClose();
				}
			};

			const cancellation = new AbortController();
			load(id, cancellation.signal);
			return () => cancellation.abort();
		},
		[
			alertManager,
			id,
			onClose
		]
	);

	const saveHandler = useCallback(
		(): void => {
			ensureNotNil(
				editTileRef.current
					?.updateHandler
			)();
		},
		[]
	);

	const afterSaveHandler = useCallback(
		(): void => {
			onAfterTileEdited();
		},
		[onAfterTileEdited]
	);

	return (
		<>
			{model && (
				<Dialog
					trackingId="edit-tile-slider"
					variant="left-slider"
					onClose={onClose}
				>
					<Dialog.Header
						title={
							<FormattedText
								text={localize("_EditNameTile")}
								thingsToReplace={[
									{ what: "{{Name}}", filler: model.name }
								]}
							/>
						}
						onClose={onClose}
						hasBorder
					/>

					<Dialog.Body>
						<div className={styles.content}>
							<EditTile
								ref={editTileRef}
								model={model}
								mode={tileEditMode}
								onAfterSave={afterSaveHandler}
								onIsBusyChanged={setIsBusy}
							/>
						</div>
					</Dialog.Body>

					<Dialog.Footer hasBorder>
						<Toolbar>
							<Toolbar.Button
								trackingId="save"
								kind="primary"
								text={localize("_Save")}
								onClick={saveHandler}
							/>

							<Toolbar.Button
								trackingId="cancel"
								kind="secondary"
								text={localize("_Cancel")}
								onClick={onClose}
							/>
						</Toolbar>
					</Dialog.Footer>
				</Dialog>
			)}
			{isBusy && (
				<Portal container="body">
					<BusyIndicator />
				</Portal>
			)}
		</>
	);
};
EditTileSlider.displayName = "EditTileSlider";

export function useEditTileSlider(): [
	element: ReactElement | null,
	open: (props: Omit<EditTileSliderProps, "onClose" | "onAfterTileEdited">) => Promise<TryResult<void>>
] {
	return useDataInputDialog(
		(props, onClose, onSave) => (
			<EditTileSlider
				{...props}
				onClose={onClose}
				onAfterTileEdited={onSave}
			/>
		)
	);
}
